import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowLeftIcon, 
  PlusIcon,
  DocumentTextIcon,
  LinkIcon,
  PlayIcon,
  SparklesIcon,
  EllipsisVerticalIcon,
  FolderIcon
} from '@heroicons/react/24/outline';
import { boardsAPI } from '../utils/api';
import toast from 'react-hot-toast';
import AddContentModal from '../components/AddContentModal';
import PerformanceTracker from '../components/PerformanceTracker';

const BoardDetailPage = ({ user }) => {
  const { boardId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [board, setBoard] = useState(null);
  const [boardItems, setBoardItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddContentModal, setShowAddContentModal] = useState(false);
  const [showPerformanceTracker, setShowPerformanceTracker] = useState(false);
  const [trackingContentId, setTrackingContentId] = useState(null);

  useEffect(() => {
    fetchBoardDetails();
    
    // Handle new content added from AI Generator
    if (location.state?.newContent) {
      const formattedContent = {
        ...location.state.newContent,
        created_at: formatTimestamp(location.state.newContent.created_at)
      };
      setBoardItems(prev => [formattedContent, ...prev]);
      
      // Clear the state so it doesn't re-add on refresh
      navigate(location.pathname, { replace: true });
      toast.success('AI-generated content added to board!');
    }
  }, [boardId, location.state]);

  const fetchBoardDetails = async () => {
    try {
      setLoading(true);
      
      // For demo boards, show sample content
      if (boardId.includes('sample')) {
        const demoBoard = {
          id: boardId,
          name: boardId === 'sample-1' ? 'YouTube Strategy' :
                boardId === 'sample-2' ? 'Instagram Content' :
                boardId === 'sample-3' ? 'Competitor Analysis' :
                boardId === 'sample-4' ? 'TikTok Trends' : 'Sample Content Board',
          description: boardId === 'sample-1' ? 'Content planning and competitor analysis for YouTube channel' :
                       boardId === 'sample-2' ? 'Reels, stories, and post ideas for Instagram growth' :
                       boardId === 'sample-3' ? 'Tracking competitor content and strategies' :
                       boardId === 'sample-4' ? 'Latest TikTok trends and viral content ideas' : 'Demo board with sample content',
          board_type: 'general',
          created_at: new Date().toISOString(),
          isDemo: true
        };
        
        // Get specific demo content based on board type
        const getDemoItems = (boardId) => {
          switch (boardId) {
            case 'sample-1': // YouTube Strategy
              return [
                {
                  id: 'demo-1',
                  title: 'How to Create Viral YouTube Shorts',
                  type: 'youtube',
                  url: 'https://youtube.com/watch?v=example1',
                  status: 'processed',
                  created_at: '2 hours ago'
                },
                {
                  id: 'demo-2',
                  title: 'YouTube Algorithm Guide 2024',
                  type: 'document',
                  url: 'youtube-algorithm-guide.pdf',
                  status: 'processed',
                  created_at: '1 day ago'
                },
                {
                  id: 'demo-3',
                  title: 'Top YouTuber Channel Analysis',
                  type: 'website',
                  url: 'https://competitor-channel.com',
                  status: 'processing',
                  created_at: '3 days ago'
                }
              ];
            case 'sample-2': // Instagram Content
              return [
                {
                  id: 'demo-4',
                  title: 'Instagram Reels Trends 2024',
                  type: 'youtube',
                  url: 'https://instagram.com/reel/example',
                  status: 'processed',
                  created_at: '1 hour ago'
                },
                {
                  id: 'demo-5',
                  title: 'Instagram Story Templates',
                  type: 'document',
                  url: 'story-templates.pdf',
                  status: 'processed',
                  created_at: '2 days ago'
                },
                {
                  id: 'demo-6',
                  title: 'Influencer Content Analysis',
                  type: 'website',
                  url: 'https://top-influencer.com',
                  status: 'processed',
                  created_at: '1 week ago'
                }
              ];
            case 'sample-3': // Competitor Analysis
              return [
                {
                  id: 'demo-7',
                  title: 'Competitor Video Breakdown',
                  type: 'youtube',
                  url: 'https://youtube.com/watch?v=competitor',
                  status: 'processed',
                  created_at: '4 hours ago'
                },
                {
                  id: 'demo-8',
                  title: 'Market Research Report Q4',
                  type: 'document',
                  url: 'market-research-q4.pdf',
                  status: 'processed',
                  created_at: '2 days ago'
                },
                {
                  id: 'demo-9',
                  title: 'Competitor Website Audit',
                  type: 'website',
                  url: 'https://main-competitor.com',
                  status: 'processing',
                  created_at: '5 days ago'
                }
              ];
            case 'sample-4': // TikTok Trends
              return [
                {
                  id: 'demo-10',
                  title: 'Viral TikTok Dance Analysis',
                  type: 'youtube',
                  url: 'https://tiktok.com/@user/video/123',
                  status: 'processed',
                  created_at: '30 minutes ago'
                },
                {
                  id: 'demo-11',
                  title: 'TikTok Trending Sounds Report',
                  type: 'document',
                  url: 'tiktok-sounds-report.pdf',
                  status: 'processed',
                  created_at: '1 day ago'
                },
                {
                  id: 'demo-12',
                  title: 'TikTok Creator Strategy Guide',
                  type: 'website',
                  url: 'https://tiktok-strategies.com',
                  status: 'processed',
                  created_at: '3 days ago'
                }
              ];
            default:
              return [
                {
                  id: 'demo-default',
                  title: 'Sample Content Item',
                  type: 'document',
                  url: 'sample.pdf',
                  status: 'processed',
                  created_at: '1 day ago'
                }
              ];
          }
        };
        
        const demoItems = getDemoItems(boardId);
        
        setBoard(demoBoard);
        setBoardItems(demoItems);
      } else {
        // For real boards, fetch from API
        const response = await fetch(`${import.meta.env.VITE_API_URL}/api/boards/${boardId}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`
          }
        });

        if (response.ok) {
          const boardData = await response.json();
          setBoard(boardData.board || {
            id: boardId,
            name: 'Loading...',
            description: 'Fetching board details...',
            board_type: 'general',
            created_at: new Date().toISOString(),
            isDemo: false
          });

          // Set board items from the same response (no separate API call needed)
          setBoardItems(boardData.items || []);
        } else {
          // Fallback board data
          setBoard({
            id: boardId,
            name: 'Board Not Found',
            description: 'This board may not exist or you may not have access',
            board_type: 'general',
            created_at: new Date().toISOString(),
            isDemo: false
          });
          setBoardItems([]);
        }
      }
    } catch (error) {
      toast.error('Failed to load board');
      console.error('Error fetching board:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddContent = () => {
    setShowAddContentModal(true);
  };

  const handleContentAdded = (newContent) => {
    // Format the timestamp for display
    const formattedContent = {
      ...newContent,
      created_at: formatTimestamp(newContent.created_at)
    };
    setBoardItems(prev => [formattedContent, ...prev]);
  };

  const formatTimestamp = (timestamp) => {
    const now = new Date();
    const time = new Date(timestamp);
    const diff = Math.abs(now - time);
    
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    
    if (minutes < 60) return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`;
    if (hours < 24) return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
    return `${days} day${days !== 1 ? 's' : ''} ago`;
  };

  const handleGenerateScript = () => {
    navigate('/ai-generator', { 
      state: { 
        boardId: board.id, 
        boardName: board.name 
      } 
    });
  };

  const handleTrackPerformance = (contentId) => {
    setTrackingContentId(contentId);
    setShowPerformanceTracker(true);
  };

  const handlePerformanceTracked = () => {
    setShowPerformanceTracker(false);
    setTrackingContentId(null);
    toast.success('Performance data tracked successfully!');
  };

  const getItemIcon = (item) => {
    const type = item.type || item.platform;
    
    // Handle generated content
    if (item.type === 'generated_content') {
      return <SparklesIcon className="h-5 w-5 text-purple-500" />;
    }
    
    // Handle file types
    if (item.fileType) {
      if (item.fileType.includes('video')) return <PlayIcon className="h-5 w-5 text-red-500" />;
      if (item.fileType.includes('audio')) return <PlayIcon className="h-5 w-5 text-green-500" />;
      return <DocumentTextIcon className="h-5 w-5 text-blue-500" />;
    }
    
    // Handle URL types
    switch (type) {
      case 'youtube':
      case 'video':
        return <PlayIcon className="h-5 w-5 text-red-500" />;
      case 'instagram':
        return <PlayIcon className="h-5 w-5 text-pink-500" />;
      case 'tiktok':
        return <PlayIcon className="h-5 w-5 text-purple-500" />;
      case 'document':
      case 'pdf':
      case 'file':
        return <DocumentTextIcon className="h-5 w-5 text-blue-500" />;
      case 'website':
      case 'url':
      case 'linkedin':
        return <LinkIcon className="h-5 w-5 text-green-500" />;
      default:
        return <DocumentTextIcon className="h-5 w-5 text-gray-500" />;
    }
  };

  const getStatusBadge = (status) => {
    const statusStyles = {
      processed: 'bg-green-100 text-green-800',
      processing: 'bg-yellow-100 text-yellow-800',
      failed: 'bg-red-100 text-red-800'
    };
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusStyles[status] || statusStyles.processed}`}>
        {status || 'processed'}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="space-y-8">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="h-4 bg-gray-200 rounded w-1/2 mb-8"></div>
        </div>
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-2 text-gray-600">Loading board...</p>
        </div>
      </div>
    );
  }

  if (!board) {
    return (
      <div className="text-center py-8">
        <FolderIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">Board not found</h3>
        <p className="text-gray-600 mb-4">The board you're looking for doesn't exist.</p>
        <button
          onClick={() => navigate('/boards')}
          className="btn-primary"
        >
          Back to Boards
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="flex items-center justify-between"
      >
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/boards')}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <ArrowLeftIcon className="h-5 w-5 text-gray-600" />
          </button>
          <div>
            <div className="flex items-center space-x-2">
              <h1 className="text-3xl font-bold text-gray-900">{board.name}</h1>
              {board.isDemo && (
                <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">
                  Demo
                </span>
              )}
              {!board.isDemo && (
                <span className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full">
                  Real
                </span>
              )}
            </div>
            <p className="mt-2 text-gray-600">
              {board.description || 'No description provided'}
            </p>
          </div>
        </div>
        
        <div className="flex items-center space-x-3">
          <button
            onClick={handleAddContent}
            className="btn-secondary flex items-center"
          >
            <PlusIcon className="w-4 h-4 mr-2" />
            Add Content
          </button>
          <button
            onClick={handleGenerateScript}
            className="btn-primary flex items-center"
          >
            <SparklesIcon className="w-4 h-4 mr-2" />
            Generate Script
          </button>
        </div>
      </motion.div>

      {/* Board Content */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
        className="card"
      >
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-medium text-gray-900">
            Board Content
          </h3>
          <button
            onClick={handleAddContent}
            className="text-sm text-primary-600 hover:text-primary-700 font-medium"
          >
            Add new content
          </button>
        </div>

        {boardItems.length === 0 ? (
          <div className="text-center py-8">
            <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <h4 className="text-lg font-medium text-gray-900 mb-2">
              No content yet
            </h4>
            <p className="text-gray-600 mb-4">
              Start by adding some content to this board
            </p>
            <button
              onClick={handleAddContent}
              className="btn-primary"
            >
              Add Content
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {boardItems.map((item, index) => (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
                className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
              >
                              <div className="flex items-center space-x-3">
                {getItemIcon(item)}
                  <div>
                    <h4 className="text-sm font-medium text-gray-900">
                      {item.title}
                    </h4>
                                      <p className="text-sm text-gray-500">
                    {(item.platform || item.type || 'Content').charAt(0).toUpperCase() + (item.platform || item.type || 'Content').slice(1)} • Added {item.created_at}
                  </p>
                    {item.url && (
                      <p className="text-xs text-gray-400 truncate max-w-xs">
                        {item.url}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  {getStatusBadge(item.status)}
                  {!board.isDemo && (
                    <button
                      onClick={() => handleTrackPerformance(item.id)}
                      className="px-3 py-1 text-xs bg-blue-100 text-blue-700 rounded-full hover:bg-blue-200 transition-colors"
                      title="Track performance"
                    >
                      📊 Track
                    </button>
                  )}
                  <button className="p-2 rounded-lg hover:bg-gray-200 transition-colors">
                    <EllipsisVerticalIcon className="h-4 w-4 text-gray-400" />
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </motion.div>

      {/* Demo Notice for Demo Boards */}
      {board.isDemo && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="bg-blue-50 border border-blue-200 rounded-lg p-4"
        >
          <div className="flex">
            <div className="flex-shrink-0">
              <FolderIcon className="h-5 w-5 text-blue-400" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-blue-800">
                Demo Board
              </h3>
              <div className="mt-2 text-sm text-blue-700">
                <p>
                  This is a demo board with sample content. Create your own boards to start organizing real content!
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      )}

      {/* Add Content Modal */}
      <AddContentModal
        isOpen={showAddContentModal}
        onClose={() => setShowAddContentModal(false)}
        boardId={board?.id}
        boardName={board?.name}
        onContentAdded={handleContentAdded}
      />

      {/* Performance Tracker Modal */}
      {showPerformanceTracker && (
        <PerformanceTracker
          contentId={trackingContentId}
          onUpdate={handlePerformanceTracked}
          onClose={() => setShowPerformanceTracker(false)}
        />
      )}
    </div>
  );
};

export default BoardDetailPage; 